import axios from 'axios';

import { AUTH_ROUTES } from './routes';
import { getAccessToken, getRefreshToken, setAuthTokens } from './token-storage';

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
}

export interface ValidateInviteResponse {
  email: string;
  shop_name: string;
  role_code: string;
}

export interface AcceptInviteRequest {
  token: string;
  password: string;
  first_name: string;
  last_name: string;
  patronymic?: string | null;
  phone?: string | null;
}

const getApiBaseUrl = (): string => {
  const defaultUrl = import.meta.env.VITE_IOKA_API_URL;
  return defaultUrl;
};

export const api = axios.create({
  baseURL: getApiBaseUrl(),
});

// not actually used
api.interceptors.request.use((config) => {
  const accessToken = getAccessToken();
  if (accessToken && config.headers) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    const requestUrl = originalRequest.url ?? '';
    const isAuthRequest =
      requestUrl.endsWith(AUTH_ROUTES.LOGIN) || requestUrl.endsWith(AUTH_ROUTES.REFRESH_TOKEN);

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true;
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        try {
          const response = await api.post(AUTH_ROUTES.REFRESH_TOKEN, {
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
          });
          const newAccessToken = response?.data?.access_token;
          const newRefreshToken = response?.data?.refresh_token ?? refreshToken;
          setAuthTokens(newAccessToken, newRefreshToken);
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }
    }
    return Promise.reject(error);
  },
);

export const loginWithPassword = async (email: string, password: string) => {
  const response = await api.post<AuthTokens>(AUTH_ROUTES.LOGIN, { email, password });
  const { access_token, refresh_token } = response.data;
  setAuthTokens(access_token, refresh_token);

  return response.data;
};

export const registerWithInvite = async (payload: AcceptInviteRequest) => {
  const response = await api.post<AuthTokens>(AUTH_ROUTES.INVITES_ACCEPT, payload);
  const { access_token, refresh_token } = response.data;
  setAuthTokens(access_token, refresh_token);
  return response.data;
};

export const validateInviteToken = async (token: string) => {
  const response = await api.get<ValidateInviteResponse>(AUTH_ROUTES.INVITES_VALIDATE, {
    params: { token },
  });
  return response.data;
};
